// 스텝3 다운로드 레이아웃 순수 로직 — 헤더명 문자열 매칭(스펙 2026-07-19 §4).
// 중복 헤더는 첫 출현만 유효 컬럼으로 취급(사영은 첫 매칭 인덱스 기준).

/** 문서 출력 헤더들 × 저장 레이아웃 → 유효 컬럼 [{header, visible}] (순서=표시 순서). */
export function mergeLayout(headers, savedColumns) {
  const seen = new Set();
  const docOrder = [];
  (headers || []).forEach((h) => {
    const k = String(h ?? "");
    if (!seen.has(k)) {
      seen.add(k);
      docOrder.push(k);
    }
  });
  const docSet = new Set(docOrder);
  const out = [];
  const used = new Set();
  (savedColumns || []).forEach((c) => {
    const k = String(c?.header ?? "");
    if (!docSet.has(k) || used.has(k)) return; // 문서에 없거나 중복 저장분 무시
    used.add(k);
    out.push({ header: k, visible: c?.visible !== false });
  });
  docOrder.forEach((k) => {
    if (!used.has(k)) out.push({ header: k, visible: true }); // 새 컬럼 뒤 추가
  });
  return out;
}

/** AoA(0행=헤더)에 유효 컬럼(순서·visible) 사영 — 다운로드 최종본. */
export function projectAoa(aoa, columns) {
  const headers = aoa?.[0] || [];
  const idxByHeader = new Map();
  headers.forEach((h, i) => {
    const k = String(h ?? "");
    if (!idxByHeader.has(k)) idxByHeader.set(k, i); // 중복=첫 매칭
  });
  const picks = (columns || [])
    .filter((c) => c.visible !== false && idxByHeader.has(String(c.header)))
    .map((c) => idxByHeader.get(String(c.header)));
  return (aoa || []).map((row) => picks.map((i) => row?.[i] ?? ""));
}

// AUIGrid는 headerText를 HTML로 렌더(E2E 실측) → 그룹 공유 잡의 crafted 엑셀 헤더
// (`<img onerror=…>`)가 타 그룹원 스텝3 열람 시 실행되는 stored XSS 벡터라 이스케이프.
// 셀 값은 텍스트 렌더 확진(updateRow로 `<b>` 주입해도 DOM 미생성)이라 그대로 둔다
// — 다운로드는 raw projectAoa 경유라 어느 쪽이든 무영향.
function escapeHtml(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// 컬럼 i가 숫자성(세번·세율·건수 등)인지 — 비어있지 않은 값의 70%+가 숫자/기호면 true.
// 문자 컬럼(상품명·성분·공급자·B/L 등)은 좌측정렬, 숫자 컬럼은 기본(가운데) 유지.
function isNumericColumn(body, i) {
  let seen = 0;
  let num = 0;
  for (const r of body) {
    const v = String(r?.[i] ?? "").trim();
    if (!v) continue;
    seen++;
    if (/^[\d.,\-\s%]+$/.test(v)) num++;
  }
  return seen > 0 && num / seen >= 0.7;
}

// 헤더 텍스트와 셀 값의 최대 글자수 기반 폭(px) — 짧은 컬럼은 좁게, 긴 컬럼은 넓게. [60,360] 클램프.
function colWidth(header, body, i) {
  let maxLen = String(header ?? "").length;
  for (const r of body) {
    const len = String(r?.[i] ?? "").length;
    if (len > maxLen) maxLen = len;
  }
  return Math.max(60, Math.min(360, maxLen * 9 + 24));
}

/** AoA → AUIGrid용 {columnDefs, rows} — field는 c{i} 인덱스 키(헤더 중복·특수문자 안전). headerText만 HTML 이스케이프.
 *  폭은 헤더/값 길이에 맞추고(colWidth), 문자 컬럼은 좌측정렬(audit-grid-left). */
export function aoaToGridData(aoa) {
  const headers = aoa?.[0] || [];
  const body = (aoa || []).slice(1);
  const columnDefs = headers.map((h, i) => {
    const def = {
      dataField: `c${i}`,
      headerText: escapeHtml(h),
      width: colWidth(h, body, i),
    };
    if (!isNumericColumn(body, i)) def.style = "audit-grid-left";
    return def;
  });
  const rows = body.map((r) => {
    const o = {};
    headers.forEach((_, i) => {
      o[`c${i}`] = r?.[i] ?? "";
    });
    return o;
  });
  return { columnDefs, rows };
}
